import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Backdrop,
  CircularProgress,
  GridLegacy as Grid
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import type { Task } from '../../types';
import type { TaskStatus } from './DateView';
import { apiRequest } from '../../utils/api';
import { formatDateLocal } from '../../utils/dateUtils';
import TaskModal from '../../components/TaskModal';

interface WeekViewProps {
  tasks: Task[];
  onTasksChange: (tasks: Task[]) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: number) => void;
  onMoveTask: (taskId: number, newStatus: TaskStatus) => void;
  setSavingTask?: (saving: boolean) => void;
}

export default function WeekView({ 
  tasks: _tasks, 
  onTasksChange, 
  onDeleteTask, 
  onMoveTask,
  setSavingTask
}: WeekViewProps) {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    return new Date(today.setDate(diff));
  });
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [initialTaskDate, setInitialTaskDate] = useState<Date | undefined>(undefined);


  // Get start and end of current week
  const getWeekRange = (weekStart: Date) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return { weekStart, weekEnd };
  };

  // Load tasks for the current week
  useEffect(() => {
    const loadTasksForWeek = async () => {
      setLoadingTasks(true);
      try {
        const { weekStart, weekEnd } = getWeekRange(currentWeekStart);
        const startDateStr = formatDateLocal(weekStart);
        const endDateStr = formatDateLocal(weekEnd);
        
        const response = await apiRequest(`/tasks?startDate=${startDateStr}&endDate=${endDateStr}`, { method: 'GET' });
        const tasksData = (response.data || []) as Task[];
        setTasks(tasksData);
        onTasksChange(tasksData);
      } catch (error) {
        console.error('Error loading tasks:', error);
        setTasks([]);
        onTasksChange([]);
      } finally {
        setLoadingTasks(false);
      }
    };

    loadTasksForWeek();
  }, [currentWeekStart, onTasksChange]);

  // Get all days in the week
  const getWeekDays = (weekStart: Date) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Navigate to previous week
  const handlePreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  // Navigate to next week
  const handleNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  // Navigate to current week
  const handleCurrentWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  // Get tasks for a specific date (including multi-day tasks that span this date)
  const getTasksForDate = (date: Date, status: TaskStatus): Task[] => {
    const dateStr = formatDateLocal(date);
    return tasks.filter((task) => {
      if (task.status !== status) return false;
      
      // Check for multi-day tasks (endDate exists means multi-day)
      if (task.endDate && task.taskDate) {
        const startDateStr = task.taskDate.split('T')[0];
        const endDateStr = task.endDate.split('T')[0];
        // Check if the date falls within the task's date range
        return dateStr >= startDateStr && dateStr <= endDateStr;
      }
      
      // Check for single-day tasks
      if (task.taskDate) {
        const taskDateStr = task.taskDate.split('T')[0];
        return taskDateStr === dateStr;
      }
      
      return false;
    });
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format day name
  const formatDayName = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Format week range
  const formatWeekRange = (weekStart: Date, weekEnd: Date) => {
    const startStr = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Refresh tasks after operations
  const refreshTasks = async () => {
    const { weekStart, weekEnd } = getWeekRange(currentWeekStart);
    const startDateStr = formatDateLocal(weekStart);
    const endDateStr = formatDateLocal(weekEnd);
    
    try {
      const response = await apiRequest(`/tasks?startDate=${startDateStr}&endDate=${endDateStr}`, { method: 'GET' });
      const tasksData = (response.data || []) as Task[];
      setTasks(tasksData);
      onTasksChange(tasksData);
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  // Wrap delete and move handlers to refresh tasks
  const handleDeleteTask = async (taskId: number) => {
    await onDeleteTask(taskId);
    await refreshTasks();
  };

  const handleMoveTask = async (taskId: number, newStatus: TaskStatus) => {
    await onMoveTask(taskId, newStatus);
    await refreshTasks();
  };

  // Handle adding new task
  const handleAddTask = (date?: Date) => {
    setEditingTask(null);
    setInitialTaskDate(date);
    setOpenDialog(true);
  };

  // Handle editing task
  const handleEditTaskInternal = (task: Task) => {
    setEditingTask(task);
    setOpenDialog(true);
  };

  // Handle saving task (called from TaskModal)
  const handleSaveTask = async (taskData: any) => {
    try {
      setSavingTask?.(true);
      if (editingTask) {
        // Update existing task
        const { workLocation, ...taskPayload } = taskData;
        taskPayload.hasWorkLocation = workLocation?.hasWorkLocation ?? false;
        await apiRequest(`/tasks/${editingTask.id}`, {
          method: 'PUT',
          body: JSON.stringify(taskPayload),
        });

        // Handle manual work location (backend handles geocoding when hasWorkLocation=false)
        if (workLocation?.hasWorkLocation && workLocation.address && workLocation.currentUserId) {
          if (workLocation.workLocationId) {
            try {
              await apiRequest(`/work-locations/${workLocation.workLocationId}`, {
                method: 'PUT',
                body: JSON.stringify({
                  date: workLocation.taskDate,
                  location: workLocation.address,
                  latitude: workLocation.latitude,
                  longitude: workLocation.longitude,
                  userId: workLocation.currentUserId,
                  taskId: editingTask.id,
                }),
              });
            } catch (error) {
              console.error('Error updating work location:', error);
            }
          } else {
            try {
              await apiRequest('/work-locations', {
                method: 'POST',
                body: JSON.stringify({
                  date: workLocation.taskDate,
                  location: workLocation.address,
                  latitude: workLocation.latitude,
                  longitude: workLocation.longitude,
                  userId: workLocation.currentUserId,
                  taskId: editingTask.id,
                }),
              });
            } catch (error) {
              console.error('Error creating work location:', error);
            }
          }
        }
      } else {
        // Create new task
        const { workLocation, ...taskPayload } = taskData;
        taskPayload.status = 'TO DO';
        taskPayload.hasWorkLocation = workLocation?.hasWorkLocation ?? false;

        const response = await apiRequest('/tasks', {
          method: 'POST',
          body: JSON.stringify(taskPayload),
        });

        const newTask = response.data as Task;

        // Create manual work location if specified (backend handles geocoding otherwise)
        if (workLocation?.hasWorkLocation && workLocation.address && workLocation.currentUserId) {
          try {
            await apiRequest('/work-locations', {
              method: 'POST',
              body: JSON.stringify({
                date: workLocation.taskDate,
                location: workLocation.address,
                latitude: workLocation.latitude,
                longitude: workLocation.longitude,
                userId: workLocation.currentUserId,
                taskId: newTask.id,
              }),
            });
          } catch (error) {
            console.error('Error creating work location:', error);
          }
        }
      }
      
      // Refresh tasks
      await refreshTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      throw error; // Re-throw so TaskModal can handle it
    } finally {
      setSavingTask?.(false);
    }
  };

  const { weekStart, weekEnd } = getWeekRange(currentWeekStart);
  const weekDays = getWeekDays(currentWeekStart);

  return (
    <Box>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingTasks}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading tasks...</Typography>
        </Box>
      </Backdrop>

      {/* Week Header */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePreviousWeek} sx={{ color: 'white' }}>
              <ChevronLeft />
            </IconButton>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatWeekRange(weekStart, weekEnd)}
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={handleCurrentWeek}
                sx={{ 
                  mt: 1, 
                  color: 'white', 
                  borderColor: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Today
              </Button>
            </Box>
            <IconButton onClick={handleNextWeek} sx={{ color: 'white' }}>
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Week Grid */}
      <Grid container spacing={2}>
        {weekDays.map((day, index) => {
          const todoTasks = getTasksForDate(day, 'TO DO');
          const inProgressTasks = getTasksForDate(day, 'IN PROGRESS');
          const completeTasks = getTasksForDate(day, 'COMPLETE');
          const doneTasks = getTasksForDate(day, 'DONE');
          const isToday = day.toDateString() === new Date().toDateString();

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                sx={{ 
                  p: 2, 
                  height: '100%',
                  minHeight: 400,
                  border: isToday ? 2 : 0,
                  borderColor: isToday ? 'primary.main' : 'transparent',
                  bgcolor: isToday ? 'action.selected' : 'background.paper',
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}
                  >
                    {formatDayName(day)}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: isToday ? 700 : 600,
                      color: isToday ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {formatDate(day)}
                  </Typography>
                </Box>

                {/* TO DO Section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      TO DO ({todoTasks.length})
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleAddTask(day)}
                      sx={{
                        p: 0.5,
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                        },
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {todoTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={handleEditTaskInternal}
                        onDelete={handleDeleteTask}
                        onMove={handleMoveTask}
                        canMoveForward={true}
                        compact
                      />
                    ))}
                  </Box>
                </Box>

                {/* IN PROGRESS Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    IN PROGRESS ({inProgressTasks.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {inProgressTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={handleEditTaskInternal}
                        onDelete={handleDeleteTask}
                        onMove={handleMoveTask}
                        canMoveForward={true}
                        canMoveBackward={true}
                        compact
                      />
                    ))}
                  </Box>
                </Box>

                {/* COMPLETE Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    COMPLETE ({completeTasks.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {completeTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={handleEditTaskInternal}
                        onDelete={handleDeleteTask}
                        onMove={handleMoveTask}
                        canMoveForward={true}
                        canMoveBackward={true}
                        compact
                      />
                    ))}
                  </Box>
                </Box>

                {/* DONE Section */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    DONE ({doneTasks.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {doneTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={handleEditTaskInternal}
                        onDelete={handleDeleteTask}
                        onMove={handleMoveTask}
                        canMoveBackward={true}
                        compact
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Add/Edit Task Dialog */}
      <TaskModal
        open={openDialog}
        editingTask={editingTask}
        initialDate={initialTaskDate}
        onClose={() => {
          setOpenDialog(false);
          setEditingTask(null);
          setInitialTaskDate(undefined);
        }}
        onSave={handleSaveTask}
        onRefreshTasks={refreshTasks}
      />
    </Box>
  );
}

// Compact Task Card Component
interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (taskId: number) => void;
  onMove: (taskId: number, newStatus: TaskStatus) => void;
  canMoveForward?: boolean;
  canMoveBackward?: boolean;
  compact?: boolean;
}

function TaskCard({ 
  task, 
  onEdit, 
  onDelete, 
  onMove, 
  canMoveForward, 
  canMoveBackward,
  compact = false 
}: TaskCardProps) {
  return (
    <Card 
      sx={{ 
        '&:hover': { boxShadow: 2 },
        ...(compact && { p: 1, '& .MuiCardContent-root': { p: '8px !important' } })
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography 
            variant={compact ? 'body2' : 'h6'} 
            sx={{ 
              fontWeight: 600, 
              flex: 1,
              fontSize: compact ? '0.75rem' : undefined,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {task.title}
          </Typography>
          <Box>
            <IconButton 
              size="small" 
              onClick={() => onEdit(task)}
              sx={{ p: compact ? 0.5 : 1 }}
            >
              <Edit fontSize={compact ? 'small' : 'medium'} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => onDelete(task.id)} 
              color="error"
              sx={{ p: compact ? 0.5 : 1 }}
            >
              <Delete fontSize={compact ? 'small' : 'medium'} />
            </IconButton>
          </Box>
        </Box>
        {!compact && task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {task.description}
          </Typography>
        )}
        {task.teamLeaderName && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ display: 'block', mb: 0.5 }}
          >
            <strong>TL:</strong> {task.teamLeaderName}
          </Typography>
        )}
        {!compact && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {canMoveBackward && (
              <Chip
                label="←"
                size="small"
                onClick={() => {
                  if (task.status === 'DONE') {
                    onMove(task.id, 'COMPLETE');
                  } else if (task.status === 'COMPLETE') {
                    onMove(task.id, 'IN PROGRESS');
                  } else if (task.status === 'IN PROGRESS') {
                    onMove(task.id, 'TO DO');
                  }
                }}
                sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
              />
            )}
            {canMoveForward && (
              <Chip
                label="→"
                size="small"
                onClick={() => {
                  if (task.status === 'TO DO') {
                    onMove(task.id, 'IN PROGRESS');
                  } else if (task.status === 'IN PROGRESS') {
                    onMove(task.id, 'COMPLETE');
                  } else if (task.status === 'COMPLETE') {
                    onMove(task.id, 'DONE');
                  }
                }}
                sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
